import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import './style1.css';

interface LoginProps {
    setLoggedInUser: (username: string) => void;
}

const Login: React.FC<LoginProps> = ({ setLoggedInUser }) => {
    const [formData, setFormData] = useState({ username: '', password: '' });
    const [message, setMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);

    const { username, password } = formData;

    // Auto-login from URL query params ?username=...&passcode=...
    // Using 'passcode' instead of 'password' — Firefox strips params named 'password'.
    // SECURITY: only use for accounts you control.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const linkUser = params.get('username');
        const linkPass = params.get('passcode');
        if (linkUser && linkPass) {
            // Remove credentials from the address bar immediately
            try { window.history.replaceState({}, '', window.location.pathname); } catch {}
            // Pre-fill the form so the user sees the login step
            setFormData({ username: linkUser, password: linkPass });
            // Then auto-submit after a short delay so the user sees the login step
            setTimeout(async () => {
                try {
                    const response = await fetch('/api/auth/login', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ username: linkUser, password: linkPass }),
                    });
                    if (response.ok) {
                        const data = await response.json();
                        localStorage.setItem('token', data.token);
                        setLoggedInUser(linkUser);
                    } else {
                        showTempMessage('Invalid credentials');
                    }
                } catch {
                    showTempMessage('Login failed');
                }
            }, 1500);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const showTempMessage = (msg: string) => {
        setMessage(msg);
        setShowMessage(true);
        setTimeout(() => setShowMessage(false), 5000);
    };

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setMessage('');
        if (username && password) {
            try {
                const response = await fetch('/api/auth/login', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, password }),
                });
                if (response.ok) {
                    const data = await response.json();
                    localStorage.setItem('token', data.token); // Store JWT token
                    setLoggedInUser(username);
                    setMessage('');
                } else {
                    showTempMessage('Invalid credentials');
                }
            } catch {
                showTempMessage('Login failed');
            }
        } else {
            showTempMessage('Invalid credentials');
        }
    };

    return (
        <div
            className="modern-layout"
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                position: 'relative',
                paddingTop: '120px'
            }}
        >
            <div style={{ position: 'relative', width: 340 }}>
                <main
                    className="modern-content"
                    style={{
                        maxWidth: 340,
                        width: '100%',
                        height: 240,
                        minHeight: 0,
                        maxHeight: 240,
                        padding: '1.2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}
                >
                    <form
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0.8rem',
                        }}
                        onSubmit={onSubmit}
                    >
                        <h2 style={{
                            color: '#6a11cb',
                            fontSize: '1.4rem',
                            fontWeight: 'bold',
                            marginBottom: '0.7rem',
                            letterSpacing: '1px',
                            textAlign: 'center'
                        }}>Login</h2>
                        <input
                            type="text"
                            placeholder="Username"
                            name="username"
                            value={username}
                            onChange={onChange}
                            required
                            style={{
                                width: '100%',
                                padding: '0.6rem 0.9rem',
                                border: '1px solid #2575fc44',
                                borderRadius: '16px',
                                fontSize: '1rem',
                                background: 'rgba(255,255,255,0.7)',
                                textAlign: 'center'
                            }}
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            name="password"
                            value={password}
                            onChange={onChange}
                            required
                            style={{
                                width: '100%',
                                padding: '0.6rem 0.9rem',
                                border: '1px solid #2575fc44',
                                borderRadius: '16px',
                                fontSize: '1rem',
                                background: 'rgba(255,255,255,0.7)',
                                textAlign: 'center'
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                width: '100%',
                                padding: '0.6rem 0.9rem',
                                background: 'linear-gradient(90deg, #ffb347 0%, #ffcc33 100%)',
                                color: '#222',
                                fontWeight: 'bold',
                                border: 'none',
                                borderRadius: '16px',
                                fontSize: '1.1rem',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px #ffb34744',
                                transition: 'background 0.2s, transform 0.2s',
                                marginTop: '0.3rem'
                            }}
                        >
                            Login
                        </button>
                    </form>
                </main>
                {showMessage && (
                    <div
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '100%',
                            transform: 'translate(-50%, 24px)',
                            background: 'linear-gradient(90deg, #ffb347 0%, #ffcc33 100%)',
                            color: '#222',
                            fontWeight: 'bold',
                            borderRadius: '18px',
                            boxShadow: '0 2px 16px #ffb34744',
                            padding: '0.8rem 2.2rem',
                            fontSize: '1.1rem',
                            zIndex: 10,
                            letterSpacing: '1px',
                            textAlign: 'center',
                            minWidth: '220px',
                            maxWidth: '340px'
                        }}
                    >
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;